import { InvalidArgumentException, InvalidOperationException } from "../Exceptions";
import { CancellationToken } from "../Promises/CancellationTokenSource";
import { IStreamReader } from "../Streams/IStreamReader";
import { IStreamEncryptor } from "./IStreamEncryptor";
/**
 * Umožňuje proudově šifrovat pod ním ležící stream při jeho čtení
 */
export class EncryptedStreamReaderDecorator implements IStreamReader {
    protected readonly _streamReader: IStreamReader;
    protected readonly _encryptor: IStreamEncryptor;

    public get size(): number {
        if (this._streamReader.size == null)
            throw new InvalidOperationException("Underlying stream reader must have determinable size");

        return this._encryptor.getEncryptedSize(this._streamReader.size);
    }

    constructor(streamReader: IStreamReader, encryptor: IStreamEncryptor) {
        this._streamReader = streamReader;
        this._encryptor = encryptor;
    }

    public async read(start?: number, end?: number, cancellationToken?: CancellationToken): Promise<ArrayBuffer> {
        // pozice čtení se musí přepočítat na pozice v originálním souboru - jinak se čte mimo rozsah
        const [startOrig, endOrig] = this._getOriginalStreamPosition(start, end);

        const block = await this._streamReader.read(startOrig, endOrig, cancellationToken);
        const isLast = endOrig == null ? block.byteLength === 0 : endOrig >= this._streamReader.size! - 1;

        return await this._encryptor.encryptBlock(block, isLast);
    }

    /**
     * Přepočítává pozice v šifrovaném streamu (větším) na pozice v originálním streamu (menším)
     * @param start Počáteční index v šifrovaném souboru
     * @param end Koncový index v šifrovaném souboru (včetně)
     */
    private _getOriginalStreamPosition(start?: number, end?: number): [number, number | undefined] {
        if (start == null)
            start = 0;
        else if (start > 0) // každá čtená část kromě první je posunutá o velikost bloku
            start -= this._encryptor.blockSize;

        if (start < 0)
            throw new InvalidOperationException(`[EncryptedStreamReaderDecorator] Unable to read from index (${start})`); //`

        if (end == null)
            return [start, end];

        if (end <= 0)
            throw new InvalidArgumentException("[EncryptedStreamReaderDecorator] Cannot read stream. Invalid range specified.");
        if (start >= end)
            throw new InvalidArgumentException("[EncryptedStreamReaderDecorator] Cannot read stream. Invalid range specified.");

        const size = end - start + 1;

        if (end >= this.size - 1) { // poslední blok - čte až do konce streamu
            end = this._streamReader.size! - 1; // výjimka by se vyhodila už v getteru this.size
        } else {
            if (size % this._encryptor.blockSize !== 0)
                throw new InvalidOperationException(`[EncryptedStreamReaderDecorator] Size of read data must be divisible by block size (${this._encryptor.blockSize}) but ${size} bytes requested`); //`

            end -= this._encryptor.blockSize;
        }

        return [start, end];
    }
}
